/**
 * @file いちごスタディの11の面を定義する。
 *
 * @what: 上部タブが並べる面 (計画 / うんどう / おんがく / 教材 / 記録 / 時間と体験 / よむ / 本だな /
 *   学びメモ / 分析 / 声かけ) の id・表示名・アイコンを持つ。計画の面は4つの見せ方 (いまのステップ / 単元 × ステップ /
 *   科目 × 難易度 / スケジュール) を持ち、`?display=` で選ぶ。見取り図と参考書ルートは計画の中の
 *   見せ方になった (代表の判断、2026-09-06)。古い `?view=map` / `?view=route` は計画の見せ方へ移す。
 *   開いている面は URL の `?view=` に持つので、id はそのまま URL に出る。
 * @why: 定義とタブの描画を分けておくと、ルート側が「いま何の面か」を判定するために
 *   コンポーネントを import せずに済む。
 */

// "motor" / "music" は領域タブ (うんどう / おんがくのホーム)。領域を足すときはここと STUDY_VIEWS に並べる。
export type StudyViewId =
  | "plan"
  | "motor"
  | "music"
  | "materials"
  | "log"
  | "time"
  | "reading"
  | "shelf"
  | "notes"
  | "analysis"
  | "voice";

/**
 * 計画の面の見せ方である。
 *
 * @what: step = いまのステップ (ステップのタブとその中身)、map = 単元 × ステップ (元の見取り図)、
 *   grid = 科目 × 難易度 (元の参考書ルートの表と一覧)、schedule = 受験のスケジュール。URL の `?display=` に出る。
 * @why: 見取り図と参考書ルートは同じ計画を別の粒度で見るものなので、タブを分けず計画の中の見せ方に
 *   した (代表の判断、2026-09-06)。step と map は学習者ごと、grid は家族共通、schedule は学習者の
 *   受験の設定を使う。
 */
export type PlanDisplay = "step" | "map" | "grid" | "schedule";

export const PLAN_DISPLAYS: readonly { id: PlanDisplay; label: string }[] = [
  { id: "step", label: "いまのステップ" },
  { id: "map", label: "単元 × ステップ" },
  { id: "grid", label: "科目 × 難易度" },
  { id: "schedule", label: "スケジュール" },
];

/** おとなの学習者を選んでいるときに使える計画の見せ方 (受験の参考書の面だけ)。 */
export const ADULT_PLAN_DISPLAYS: readonly PlanDisplay[] = ["grid", "schedule"];

/** 古い URL (`?view=map` / `?view=route`) を計画の見せ方へ移す対応である。 */
export const LEGACY_VIEW_TO_PLAN_DISPLAY: Readonly<Record<string, PlanDisplay>> = {
  map: "map",
  route: "grid",
};

/** URL の値が計画の見せ方かを判定する。無効なら呼び手は既定 (step) にする。 */
export function isPlanDisplay(v: string | null): v is PlanDisplay {
  return v !== null && PLAN_DISPLAYS.some((d) => d.id === v);
}

export interface StudyView {
  id: StudyViewId;
  label: string;
  icon: string;
}

export const STUDY_VIEWS: readonly StudyView[] = [
  { id: "plan", label: "計画", icon: "🗺️" },
  { id: "motor", label: "うんどう", icon: "🤸" },
  { id: "music", label: "おんがく", icon: "🎻" },
  { id: "materials", label: "教材", icon: "📚" },
  { id: "log", label: "記録", icon: "✍️" },
  { id: "time", label: "時間と体験", icon: "⚖️" },
  { id: "reading", label: "よむ", icon: "📕" },
  { id: "shelf", label: "本だな", icon: "📖" },
  { id: "notes", label: "学びメモ", icon: "📝" },
  { id: "analysis", label: "分析", icon: "🔍" },
  { id: "voice", label: "声かけ", icon: "💬" },
];

/**
 * お子さんの選択が必要な面 (学習者ごとの plan・記録に紐づく面)。
 *
 * @what: 選んだ学習者の plan を読み書きする面を並べる。route はこの一覧の面でだけ、
 *   学習者と plan の読み込み中・読み込み失敗を本文で案内する
 * @why: 面の一覧 (STUDY_VIEWS) と同じ場所に置き、領域タブを足すときに route 側の
 *   文字列を直し忘れないようにする。
 */
export const LEARNER_SCOPED_VIEW_IDS: readonly StudyViewId[] = [
  "plan",
  "motor",
  "music",
  "materials",
  "log",
];

/**
 * おとな (養育者) の学習者を選んでいるときに使える面を並べる。
 *
 * @what: おとなの選択中にタブへ並べる面の一覧を持つ (計画と本だなと声かけ。計画は受験の参考書の
 *   見せ方だけ)
 * @why: 養育者の学習 (epic_23 §10) は自炊した本を読むことが起点なので、面は本だなだけに
 *   する (代表の判断、2026-08-29)。計画・見取り図・うんどう・おんがく・よむは子ども向けの設計、
 *   教材・記録は到達度モデルと結びついた子の学習管理、時間と体験・分析は子の予定と
 *   入試の面で、いずれも混ぜない。記録の動線が必要になったら本だな側に足す。
 *   参考書ルートは家族の持ち物で学習者に従属せず、大学入試のルートは養育者が自分の学習の
 *   参考にも使うので、本だなと同じくおとなでも開ける。声かけは読み物で、学習者を選ばず
 *   記録も持たないので、どちらを選んでいても同じものが出る (代表の判断、2026-09-11)。
 *   学びメモは、本に紐づかない調べもののメモもタグで繋いで読み返す面で、大人と子で分けない
 *   (代表の判断、2026-10-29)。
 * @failure: ここに子ども向けの面を足すと、到達度モデルや「よむ」(冊数を出さない設計) に
 *   おとなの記録が混ざる。route はこの一覧に無い面をおとなでは開けないよう本だなへ移す
 *   ので、一覧を空にしてはならない (移り先が無くなる)。
 */
export const ADULT_STUDY_VIEW_IDS: readonly StudyViewId[] = ["plan", "shelf", "notes", "voice"];
